import {http, HttpResponse} from 'msw';
import {SetupServerApi} from 'msw/node';

import {mergeDeepRight} from 'ramda';

import {GetDirectSaleLabourItemCalculatePriceResponse} from '../../metadaWarehouseDirectSaleItemLabour/types';
import {
  ArticlePriceIndicator,
  GetDirectSaleMaterialItemCalculatePriceResponse,
} from '../../metadaWarehouseDirectSaleItemSparePart/types';

const getDirectSaleMaterialItemRecalculatedPricesMock = (
  pricesMock: Partial<GetDirectSaleMaterialItemCalculatePriceResponse> = {}
): GetDirectSaleMaterialItemCalculatePriceResponse =>
  mergeDeepRight(
    {
      articlePrice: {
        withVat: {
          amount: 0,
          currency: 'EUR',
        },
        withoutVat: {
          amount: 0,
          currency: 'EUR',
        },
      },
      articlePriceIndicator: 'NO_PRICE' as ArticlePriceIndicator,
      sellingPricePerUnit: {
        amount: 15,
        currency: 'EUR',
      },
      sellingTotalPrice: {
        withVat: {
          amount: 18.15,
          currency: 'EUR',
        },
        withoutVat: {
          amount: 15,
          currency: 'EUR',
        },
      },
      purchaseTotalPrice: {
        withVat: {
          amount: 12.1,
          currency: 'EUR',
        },
        withoutVat: {
          amount: 10,
          currency: 'EUR',
        },
      },
      marginTotal: {
        marginPercentage: 0.3333,
        withVat: {
          amount: 6.05,
          currency: 'EUR',
        },
        withoutVat: {
          amount: 5,
          currency: 'EUR',
        },
        isMarginUnderMinimum: false,
        isMarginExisting: true,
      },
      marginPerUnit: {
        marginPercentage: 0.3333,
        withVat: {
          amount: 6.05,
          currency: 'EUR',
        },
        withoutVat: {
          amount: 5,
          currency: 'EUR',
        },
        isMarginUnderMinimum: false,
        isMarginExisting: true,
      },
      discount: {
        amount: 0,
        currency: 'EUR',
      },
      isCustomPrice: true,
      itemsDiscounts: {
        isDoNotApplyDiscount: false,
        isDiscountEnable: false,
      },
    },
    pricesMock
  );

const materialQuantityRecalculatedPricesMock: Partial<GetDirectSaleMaterialItemCalculatePriceResponse> =
  {
    sellingTotalPrice: {
      withVat: {
        amount: 36.3,
        currency: 'EUR',
      },
      withoutVat: {
        amount: 30,
        currency: 'EUR',
      },
    },
    purchaseTotalPrice: {
      withVat: {
        amount: 24.2,
        currency: 'EUR',
      },
      withoutVat: {
        amount: 20,
        currency: 'EUR',
      },
    },
    marginTotal: {
      marginPercentage: 0.3333,
      withVat: {
        amount: 12.1,
        currency: 'EUR',
      },
      withoutVat: {
        amount: 10,
        currency: 'EUR',
      },
      isMarginUnderMinimum: false,
      isMarginExisting: true,
    },
    marginPerUnit: {
      marginPercentage: 0.3333,
      withVat: {
        amount: 6.05,
        currency: 'EUR',
      },
      withoutVat: {
        amount: 5,
        currency: 'EUR',
      },
      isMarginUnderMinimum: false,
      isMarginExisting: true,
    },
  };

const materialPricePerUnitRecalculatedPriceMock: Partial<GetDirectSaleMaterialItemCalculatePriceResponse> =
  {
    sellingPricePerUnit: {
      amount: 2,
      currency: 'EUR',
    },
    sellingTotalPrice: {
      withVat: {
        amount: 2.42,
        currency: 'EUR',
      },
      withoutVat: {
        amount: 2,
        currency: 'EUR',
      },
    },
    purchaseTotalPrice: {
      withVat: {
        amount: 12.1,
        currency: 'EUR',
      },
      withoutVat: {
        amount: 10,
        currency: 'EUR',
      },
    },
    marginTotal: {
      marginPercentage: -4,
      withVat: {
        amount: -9.68,
        currency: 'EUR',
      },
      withoutVat: {
        amount: -8,
        currency: 'EUR',
      },
      isMarginUnderMinimum: true,
      isMarginExisting: true,
    },
    marginPerUnit: {
      marginPercentage: -4,
      withVat: {
        amount: -9.68,
        currency: 'EUR',
      },
      withoutVat: {
        amount: -8,
        currency: 'EUR',
      },
      isMarginUnderMinimum: true,
      isMarginExisting: true,
    },
    isCustomPrice: true,
  };

const materialDiscountRecalculatedPriceMock: Partial<GetDirectSaleMaterialItemCalculatePriceResponse> =
  {
    sellingTotalPrice: {
      withVat: {
        amount: 9.08,
        currency: 'EUR',
      },
      withoutVat: {
        amount: 7.5,
        currency: 'EUR',
      },
    },
    marginTotal: {
      marginPercentage: -0.3333,
      withVat: {
        amount: -3.02,
        currency: 'EUR',
      },
      withoutVat: {
        amount: -2.5,
        currency: 'EUR',
      },
      isMarginUnderMinimum: true,
      isMarginExisting: true,
    },
    marginPerUnit: {
      marginPercentage: -0.3333,
      withVat: {
        amount: -3.02,
        currency: 'EUR',
      },
      withoutVat: {
        amount: -2.5,
        currency: 'EUR',
      },
      isMarginUnderMinimum: true,
      isMarginExisting: true,
    },
    discount: {
      amount: 7.5,
      currency: 'EUR',
    },
    itemsDiscounts: {
      isDoNotApplyDiscount: false,
      isDiscountEnable: true,
    },
  };

const materialGetCurrentPriceRecalculatedPricesMock: Partial<GetDirectSaleMaterialItemCalculatePriceResponse> =
  {
    sellingPricePerUnit: {
      amount: 1.21,
      currency: 'EUR',
    },
  };

const postDirectSaleMaterialItemCalculatePriceInterceptor = (
  server: SetupServerApi,
  pricesMock: Partial<GetDirectSaleMaterialItemCalculatePriceResponse> = {}
) =>
  server.use(
    http.post('*/warehouse/v1/direct-sale/*/item-spare-part/*/calculate-price', () =>
      HttpResponse.json(getDirectSaleMaterialItemRecalculatedPricesMock(pricesMock))
    )
  );

const getDirectSaleLabourItemRecalculatedPricesMock = (
  pricesMock: Partial<GetDirectSaleLabourItemCalculatePriceResponse> = {}
): GetDirectSaleLabourItemCalculatePriceResponse =>
  mergeDeepRight(
    {
      sellingPricePerUnit: {
        amount: 150,
        currency: 'CZK',
      },
      sellingTotalPrice: {
        withVat: {
          amount: 181.5,
          currency: 'CZK',
        },
        withoutVat: {
          amount: 150,
          currency: 'CZK',
        },
      },
      purchaseTotalPrice: {
        withVat: {
          amount: 0,
          currency: 'CZK',
        },
        withoutVat: {
          amount: 0,
          currency: 'CZK',
        },
      },
      marginTotal: {
        marginPercentage: 1,
        withVat: {
          amount: 181.5,
          currency: 'CZK',
        },
        withoutVat: {
          amount: 150,
          currency: 'CZK',
        },
        isMarginUnderMinimum: false,
        isMarginExisting: true,
      },
      marginPerUnit: {
        marginPercentage: 1,
        withVat: {
          amount: 181.5,
          currency: 'CZK',
        },
        withoutVat: {
          amount: 150,
          currency: 'CZK',
        },
        isMarginUnderMinimum: false,
        isMarginExisting: true,
      },
      discount: {
        amount: 0,
        currency: 'CZK',
      },
      isCustomPrice: false,
      itemsDiscounts: {
        isDoNotApplyDiscount: false,
        isDiscountEnable: false,
      },
    },
    pricesMock
  );

const labourQuantityRecalculatedPricesMock: Partial<GetDirectSaleLabourItemCalculatePriceResponse> =
  {
    sellingTotalPrice: {
      withVat: {
        amount: 363,
        currency: 'CZK',
      },
      withoutVat: {
        amount: 300,
        currency: 'CZK',
      },
    },
    marginTotal: {
      marginPercentage: 1,
      withVat: {
        amount: 363,
        currency: 'CZK',
      },
      withoutVat: {
        amount: 300,
        currency: 'CZK',
      },
      isMarginUnderMinimum: false,
      isMarginExisting: true,
    },
    marginPerUnit: {
      marginPercentage: 1,
      withVat: {
        amount: 181.5,
        currency: 'CZK',
      },
      withoutVat: {
        amount: 150,
        currency: 'CZK',
      },
      isMarginUnderMinimum: false,
      isMarginExisting: true,
    },
  };

const labourPricePerUnitRecalculatedPricesMock: Partial<GetDirectSaleLabourItemCalculatePriceResponse> =
  {
    sellingPricePerUnit: {
      amount: 300,
      currency: 'CZK',
    },
    sellingTotalPrice: {
      withVat: {
        amount: 363,
        currency: 'CZK',
      },
      withoutVat: {
        amount: 300,
        currency: 'CZK',
      },
    },
    marginTotal: {
      marginPercentage: 1,
      withVat: {
        amount: 363,
        currency: 'CZK',
      },
      withoutVat: {
        amount: 300,
        currency: 'CZK',
      },
      isMarginUnderMinimum: false,
      isMarginExisting: true,
    },
    marginPerUnit: {
      marginPercentage: 1,
      withVat: {
        amount: 363,
        currency: 'CZK',
      },
      withoutVat: {
        amount: 300,
        currency: 'CZK',
      },
      isMarginUnderMinimum: false,
      isMarginExisting: true,
    },
    discount: {
      amount: 0,
      currency: 'CZK',
    },
    isCustomPrice: true,
    itemsDiscounts: {
      isDoNotApplyDiscount: false,
      isDiscountEnable: false,
    },
    minimalWorkMargin: 0,
  };

const labourDiscountRecalculatedPriceMock: Partial<GetDirectSaleLabourItemCalculatePriceResponse> =
  {
    sellingPricePerUnit: {
      amount: 150,
      currency: 'CZK',
    },
    sellingTotalPrice: {
      withVat: {
        amount: 90.75,
        currency: 'CZK',
      },
      withoutVat: {
        amount: 75,
        currency: 'CZK',
      },
    },
    marginTotal: {
      marginPercentage: 1,
      withVat: {
        amount: 90.75,
        currency: 'CZK',
      },
      withoutVat: {
        amount: 75,
        currency: 'CZK',
      },
      isMarginUnderMinimum: false,
      isMarginExisting: true,
    },
    marginPerUnit: {
      marginPercentage: 1,
      withVat: {
        amount: 90.75,
        currency: 'CZK',
      },
      withoutVat: {
        amount: 75,
        currency: 'CZK',
      },
      isMarginUnderMinimum: false,
      isMarginExisting: true,
    },
    discount: {
      amount: 75,
      currency: 'CZK',
    },
    isCustomPrice: true,
    itemsDiscounts: {
      isDoNotApplyDiscount: false,
      isDiscountEnable: true,
    },
    minimalWorkMargin: 0,
  };

const labourGetCurrentPriceRecalculatedPricesMock: Partial<GetDirectSaleLabourItemCalculatePriceResponse> =
  {
    sellingPricePerUnit: {
      amount: 150,
      currency: 'CZK',
    },
  };

const postDirectSaleLabourItemCalculatePriceInterceptor = (
  server: SetupServerApi,
  pricesMock: Partial<GetDirectSaleLabourItemCalculatePriceResponse> = {}
) =>
  server.use(
    http.post('*/warehouse/v1/direct-sale/*/item-labour/*/calculate-price', () =>
      HttpResponse.json(getDirectSaleLabourItemRecalculatedPricesMock(pricesMock))
    )
  );

export const metadaWarehouseDirectSaleItemCalculatePriceApiMock = {
  materialQuantityRecalculatedPricesMock,
  materialPricePerUnitRecalculatedPriceMock,
  materialDiscountRecalculatedPriceMock,
  materialGetCurrentPriceRecalculatedPricesMock,
  getDirectSaleMaterialItemRecalculatedPricesMock,
  postDirectSaleMaterialItemCalculatePriceInterceptor,
  labourQuantityRecalculatedPricesMock,
  labourPricePerUnitRecalculatedPricesMock,
  labourDiscountRecalculatedPriceMock,
  labourGetCurrentPriceRecalculatedPricesMock,
  getDirectSaleLabourItemRecalculatedPricesMock,
  postDirectSaleLabourItemCalculatePriceInterceptor,
};
